import React, { useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField, ConditionOption, DialogVariable, getCachedDialogs } from '@/lib/stores/dialogStore';
import { getTypeItemById } from '@/lib/utils/typeStoreResolver';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useFieldEditingHandlers, FieldTextInput } from './BaseFieldEditor';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DialogConditionOptionEditor } from '../DialogConditionOptionEditor';
import { ConnectionBadge } from './ConnectionBadge';

export interface ConditionFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentSectionFields?: DialogField[];
  currentDialogId?: string;
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  onAddConditionOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<ConditionOption>) => void;
  onRemoveConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  gameId?: string;
  t: any;
}

/**
 * ConditionFieldEditor handles 'condition' field type
 * Includes variable selection and condition options with drag-and-drop
 */
export const ConditionFieldEditor = React.memo(function ConditionFieldEditor({
  field,
  fieldIndex,
  sectionIndex,
  sectionIdentifiers,
  sections,
  currentSectionFields,
  currentDialogId,
  dialogTranslatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  onUpdateField,
  onRemoveField,
  onAddConditionOption,
  onUpdateConditionOption,
  onRemoveConditionOption,
  gameId,
  t,
}: ConditionFieldEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  const { locaKey, displayText, sourceText, isTranslationMode, handleTextChange, handleTextBlur } = useFieldEditingHandlers(
    field, effectiveGameId, onUpdateField, sectionIndex
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Sensors for condition option dragging
  const conditionOptionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );


  // Handle condition option drag end
  const handleConditionOptionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !field.conditionOptions) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Extract option indices from IDs
    const activeOptionIndex = parseInt(activeId.split('-')[4]);
    const overOptionIndex = parseInt(overId.split('-')[4]);

    if (activeOptionIndex !== -1 && overOptionIndex !== -1) {
      const reorderedOptions = arrayMove(field.conditionOptions, activeOptionIndex, overOptionIndex);
      // Update order indices
      const updatedOptions = reorderedOptions.map((opt, i) => ({
        ...opt,
        order_index: i,
      }));
      onUpdateField(sectionIndex, field.id, { conditionOptions: updatedOptions });
    }
  }, [field.conditionOptions, sectionIndex, field.id, onUpdateField]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 border border-gray-200 rounded-sm bg-white"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-2"
        type="button"
      >
        <IconProvider iconstring="fa-grip-vertical" size={14} />
      </button>

      {/* Field Content */}
      <div className="flex-1 items-center justify-center space-y-2">
        {/* Field Text with Translation Icon */}
        <FieldTextInput
          field={field}
          displayText={displayText}
          locaKey={locaKey}
          sourceText={sourceText}
          isTranslationMode={isTranslationMode}
          dialogTranslatable={dialogTranslatable}
          onTextChange={handleTextChange}
          onTextBlur={handleTextBlur}
          onTypeChange={(type) => onUpdateField(sectionIndex, field.id, { type })}
          placeholder="Enter condition description..."
          gameId={effectiveGameId}
        />

        {/* Condition Configuration */}
        <div className="space-y-3 bg-orange-50 p-3 rounded border border-orange-200">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <IconProvider iconstring="fa-code-branch" size={14} />
            Condition Configuration
          </Label>

          {/* Variable Selection */}
          <div className="space-y-2 bg-white p-2 rounded border border-orange-300">
            <Label className="text-xs text-gray-600">Check variable:</Label>
            {dialogVariables && dialogVariables.filter(v => v.name.trim()).length > 0 ? (
              <Select
                value={field.condition?.variable || ''}
                onValueChange={(value) => {
                  const selectedVar = dialogVariables.find(v => v.name === value);
                  // Initialize conditionOptions based on variable type
                  let initialOptions = field.conditionOptions || [];
                  if (selectedVar?.type === 'boolean' && initialOptions.length === 0) {
                    // Pre-populate with true and false options for boolean
                    initialOptions = [
                      {
                        id: `${field.id}-opt-true`,
                        condition_value: true,
                        operator: '==',
                        order_index: 0,
                      },
                      {
                        id: `${field.id}-opt-false`,
                        condition_value: false,
                        operator: '==',
                        order_index: 1,
                      },
                    ];
                  } else if (selectedVar?.type === 'range-selector' && selectedVar.typeStoreRef?.typeId && initialOptions.length === 0) {
                    // Pre-populate with range options from TypeItem
                    const typeItem = getTypeItemById(selectedVar.typeStoreRef.typeId);
                    const ranges = typeItem?.data?.filter(el => el.valueType === 'range') || [];
                    initialOptions = ranges.map((range, idx) => ({
                      id: `${field.id}-range-${range.id}`,
                      condition_value: range.id,
                      operator: 'in_range' as const,
                      order_index: idx,
                    }));
                  }
                  onUpdateField(sectionIndex, field.id, {
                    condition: { variable: value },
                    conditionOptions: initialOptions,
                  });
                }}
              >
                <SelectTrigger className="h-8 text-sm font-mono">
                  <SelectValue placeholder="Select variable..." />
                </SelectTrigger>
                <SelectContent>
                  {dialogVariables.filter(v => v.name.trim()).map((v) => (
                    <SelectItem key={v.name} value={v.name}>
                      {v.name} ({v.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={field.condition?.variable || ''}
                onChange={(e) =>
                  onUpdateField(sectionIndex, field.id, {
                    condition: { variable: e.target.value },
                  })
                }
                placeholder="variable_name"
                className="h-8 text-sm font-mono"
              />
            )}
          </div>

          {/* Check Variable Selection (for range type or range-selector) */}
          {field.condition?.variable && (() => {
            const selectedVar = dialogVariables?.find(v => v.name === field.condition?.variable);
            return (selectedVar?.type === 'range' || selectedVar?.type === 'range-selector') ? (
              <div className="space-y-2 bg-purple-50 p-2 rounded border border-purple-300">
                <Label className="text-xs text-purple-700 flex items-center gap-1">
                  <IconProvider iconstring="fa-crosshairs" size={10} />
                  Variable to check (which value falls within the range):
                </Label>
                {dialogVariables && dialogVariables.filter(v => v.name.trim()).length > 0 ? (
                  <Select
                    value={field.condition?.checkVariable || ''}
                    onValueChange={(value) => {
                      onUpdateField(sectionIndex, field.id, {
                        condition: { 
                          ...field.condition,
                          checkVariable: value 
                        },
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm font-mono">
                      <SelectValue placeholder="Select variable to check..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dialogVariables
                        .filter(v => v.name.trim() && (v.type === 'number' || v.type === 'range'))
                        .map((v) => (
                          <SelectItem key={v.name} value={v.name}>
                            {v.name} ({v.type})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={field.condition?.checkVariable || ''}
                    onChange={(e) =>
                      onUpdateField(sectionIndex, field.id, {
                        condition: { 
                          ...field.condition,
                          checkVariable: e.target.value 
                        },
                      })
                    }
                    placeholder="variable_name"
                    className="h-8 text-sm font-mono"
                  />
                )}
              </div>
            ) : null;
          })()}

          {/* Condition Options */}
          {field.condition?.variable && (
            <>
              <Label className="text-xs text-gray-600">Options:</Label>
              <DndContext
                sensors={conditionOptionSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleConditionOptionDragEnd}
              >
                <SortableContext
                  items={(field.conditionOptions || []).map((_, idx) => `condition-option-${sectionIndex}-${field.id}-${idx}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {(field.conditionOptions || []).map((option, optionIndex) => {
                      const selectedVar = dialogVariables?.find(v => v.name === field.condition?.variable);
                      return (
                        <DialogConditionOptionEditor
                          key={`condition-option-${sectionIndex}-${field.id}-${optionIndex}`}
                          option={option}
                          optionIndex={optionIndex}
                          sectionIndex={sectionIndex}
                          fieldId={field.id}
                          sectionIdentifiers={sectionIdentifiers}
                          sections={sections}
                          currentDialogId={currentDialogId}
                          variableType={selectedVar?.type || 'number'}
                          variable={selectedVar}
                          onUpdateOption={onUpdateConditionOption}
                          onRemoveOption={onRemoveConditionOption}
                          gameId={effectiveGameId}
                          t={t}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>

              {/* Add Option Button */}
              <Button
                onClick={() => onAddConditionOption(sectionIndex, field.id)}
                variant="outline"
                size="sm"
                className="w-full"
                type="button"
              >
                <IconProvider iconstring="fa-plus" size={12} className="mr-2" />
                Add Condition Option
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Field Type & Buttons */}
      <div className="flex items-center gap-1">
        {/* Connection Badge */}
        <ConnectionBadge
          field={field}
          sectionIndex={sectionIndex}
          sectionIdentifiers={sectionIdentifiers}
          sections={sections}
          currentSectionFields={currentSectionFields}
          currentDialogId={currentDialogId}
          effectiveGameId={effectiveGameId}
          onUpdateField={onUpdateField}
          showFieldOption={true}
        />
        {/* Remove Field Button */}
        <Button
          variant="halfghost"
          size="sm"
          onClick={() => onRemoveField(sectionIndex, field.id)}
          className="px-2 text-xs w-8 h-8"
          type="button"
        >
          <IconProvider
            iconstring="fa-xmark"
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      </div>
    </div>
  );
});
